import { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Popover, Input, Checkbox, Modal, InputField, Button } from '@ets/design-system';
import cx from 'classnames';

import { useDispatch, useSelector } from '_common/hooks';
import { toast } from 'utils/toast';

import {
  EditElementStatusProps,
  selectElementStatusById,
  useCreateElementStatusMutation,
  useEditElementStatusMutation,
} from '_common/services/api/elementStatusApi';
import { closeModal, updateModal } from '_common/modals/ModalsSlice';

import styles from './ElementStatusFormModal.module.scss';

const COLORS = {
  grey: { background: '#F9F9FA', border: '#BFBFCC' },
  yellow: { background: '#FFFCF3', border: '#FFE28C' },
  green: { background: '#F7FCF8', border: '#B2E0BA' },
  blue: { background: '#F6F9FF', border: '#A1C4FF' },
  red: { background: '#FFF8F9', border: '#FFB7C1' },
  purple: { background: '#FAF8FF', border: '#CFBEFF' },
  orange: { background: '#FFFAF7', border: '#FFCEAC' },
  teal: { background: '#F9FDFD', border: '#BCEAED' },
};

const MESSAGES: { [key: string]: { header: string; button: string } } = {
  create: {
    header: 'ADD_ELEMENT_STATUS',
    button: 'ADD_STATUS',
  },
  edit: {
    header: 'EDIT_ELEMENT_STATUS',
    button: 'UPDATE_STATUS',
  },
};

const ElementStatusFormModal = () => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const [createElementStatus] = useCreateElementStatusMutation();
  const [editElementStatus] = useEditElementStatusMutation();
  const isOpenModal = useSelector((state) => state.modals.open.ElementStatusFormModal);
  const mode = useSelector((state) => state.modals.ElementStatusFormModal.mode);
  const id = useSelector((state) => state.modals.ElementStatusFormModal.id);
  const status = useSelector((state) => selectElementStatusById(state, id));

  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');
  const [color, setColor] = useState<keyof typeof COLORS>('green');
  const [visible, setVisible] = useState(true);
  const [editable, setEditable] = useState(true);
  const [movable, setMovable] = useState(true);
  const [deletable, setDeletable] = useState(true);
  const [confirmInput, setConfirmInput] = useState(false);
  const [permissionsDisabled, setPermissionsDisabled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, [isOpen]);

  useEffect(() => {
    if (status) {
      setName(status.name);
      setColor(status.color);
      setVisible(status.visible);
      setEditable(status.allow_edit);
      setDeletable(status.allow_delete);
      setConfirmInput(status.confirm_input);
      setMovable(status.allow_move);
      setPermissionsDisabled(status.id === 'active' || status.id === 'approved');
    } else {
      setPermissionsDisabled(false);
    }
  }, [status]);

  const onNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length > 16) {
      setNameError(intl.formatMessage({ id: 'ERROR_CHARACTER_LIMIT' }, { number: 16 }));
    } else {
      setNameError('');
    }
    setName(e.target.value);
  };
  const submit = async () => {
    const payload: EditElementStatusProps['status'] = {
      name,
      color,
      visible,
      allow_move: movable,
      allow_delete: deletable,
      allow_edit: editable,
      confirm_input: confirmInput,
    };

    if (permissionsDisabled) {
      delete payload.allow_edit;
    }

    if (mode === 'create') {
      await createElementStatus(payload);
      toast({
        variant: 'success',
        title: 'ELEMENT_STATUS_CREATED_TITLE',
        message: 'ELEMENT_STATUS_CREATED_DESCRIPTION',
        messageValues: { name },
      });
      close();
    } else if (mode === 'edit') {
      await editElementStatus({ id, status: payload });
      toast({
        variant: 'success',
        title: 'ELEMENT_STATUS_EDITED_TITLE',
        message: 'ELEMENT_STATUS_EDITED_DESCRIPTION',
        messageValues: { name },
      });
      close();
    }
  };

  const isSubmitDisabled = () => {
    if (name === '' || nameError !== '') {
      return true;
    }
    if (mode === 'edit') {
      return (
        name === status?.name &&
        color === status?.color &&
        visible === status?.visible &&
        editable === status.allow_edit &&
        movable === status.allow_move &&
        deletable === status.allow_delete &&
        confirmInput === status.confirm_input
      );
    }
    return false;
  };
  const close = () => {
    setName('');
    setColor('green');
    setVisible(true);
    setEditable(true);
    setMovable(true);
    setDeletable(true);
    setConfirmInput(false);
    dispatch(updateModal({ modal: 'ElementStatusFormModal', data: { mode: 'create', id: null } }));
    dispatch(closeModal('ElementStatusFormModal'));
  };
  return (
    <Modal open={!!isOpenModal} width="63.6rem" close={close} testId={`${mode}-element-status`}>
      <Modal.Header>
        <FormattedMessage id={MESSAGES[mode].header} />
      </Modal.Header>
      <Modal.Body>
        <div className={styles.area}>
          <div className={styles.header}>
            <FormattedMessage id="STATUS_GENERAL_INFORMATION" />
          </div>
          <div className={styles.fields}>
            <InputField
              style={{ width: '38.5rem' }}
              size="l"
              label={intl.formatMessage({ id: 'STATUS_NAME' })}
              feedbackContent={nameError}
              testId={`${mode}-element-status-name`}
            >
              <Input
                size="l"
                value={name}
                onChange={onNameChange}
                placeholder={intl.formatMessage({ id: 'ENTER_STATUS_NAME' })}
                hasError={!!nameError}
                testId={`${mode}-element-status-name`}
                ref={inputRef}
              />
            </InputField>
            <InputField
              style={{ width: '14rem', margin: '0 0 0 3rem' }}
              size="l"
              label={intl.formatMessage({ id: 'STATUS_COLOR' })}
              testId={`${mode}-element-status-color`}
            >
              <>
                <Popover.Root
                  placement="bottom-end"
                  open={isOpen}
                  onOpenChange={() => setIsOpen(!isOpen)}
                >
                  <Popover.Trigger asChild>
                    <Button
                      variant="standard"
                      size="l"
                      onClick={() => setIsOpen(!isOpen)}
                      fullWidth
                      testId={`${mode}-element-status-color`}
                    >
                      <div
                        className={styles.color}
                        style={{
                          background: COLORS[color].background,
                          border: `1px solid ${COLORS[color].border}`,
                        }}
                      />
                    </Button>
                  </Popover.Trigger>
                  <Popover.Content
                    container={
                      (document.querySelector('[data-slot="modal-content"]') as HTMLElement) ??
                      document.body
                    }
                    testId="status-color"
                    style={{ minWidth: 'auto' }}
                  >
                    <div className={styles.squares}>
                      {Object.typedKeys(COLORS).map((c) => (
                        <div
                          data-selected={c === color ? true : false}
                          className={cx(styles.square, c === color && styles.selected)}
                          style={{
                            background: COLORS[c].background,
                            border: `1px solid ${COLORS[c].border}`,
                          }}
                          key={c}
                          onClick={() => {
                            setColor(c);
                            setIsOpen(false);
                          }}
                          data-testid={`${c}-square`}
                        />
                      ))}
                    </div>
                  </Popover.Content>
                </Popover.Root>
              </>
            </InputField>
          </div>
          <Checkbox
            size="s"
            checked={visible}
            onChange={() => setVisible((state) => !state)}
            style={{ margin: '2rem 0 0 0' }}
            testId={`${mode}-element-status-checkbox-visible-status`}
          >
            <FormattedMessage id="STATUS_VISIBLE_IN_EXPLORER" />
          </Checkbox>
        </div>
        <div className={styles.area}>
          <div className={styles.header}>
            <FormattedMessage id="global.permissions" />
          </div>
          <Checkbox
            disabled={permissionsDisabled}
            size="s"
            checked={editable}
            onChange={() => setEditable((state) => !state)}
            style={{ margin: '2rem 0 0 0' }}
            testId={`${mode}-element-status-checkbox-element-can-be-edited`}
          >
            <FormattedMessage id="ELEMENTS_STATUS_CAN_BE_EDITED" />
          </Checkbox>
          <Checkbox
            size="s"
            checked={movable}
            onChange={() => setMovable((state) => !state)}
            style={{ margin: '1rem 0 0 0' }}
            testId={`${mode}-element-status-checkbox-element-can-be-moved`}
          >
            <FormattedMessage id="ELEMENTS_STATUS_CAN_BE_MOVED" />
          </Checkbox>
          <Checkbox
            size="s"
            checked={deletable}
            onChange={() => setDeletable((state) => !state)}
            style={{ margin: '1rem 0 0 0' }}
            testId={`${mode}-element-status-checkbox-element-can-be-deleted`}
          >
            <FormattedMessage id="ELEMENTS_STATUS_CAN_BE_DELETED" />
          </Checkbox>
        </div>
        <div className={styles.area}>
          <div className={styles.header}>
            <FormattedMessage id="CONFIRMATION_REQUEST" />
          </div>
          <Checkbox
            size="s"
            checked={confirmInput}
            onChange={() => setConfirmInput((state) => !state)}
            style={{ margin: '2rem 0 0 0' }}
            testId={`${mode}-element-status-checkbox-ask-confirmation`}
          >
            <FormattedMessage id="ASK_CONFIRMATION_WHEN_CHANGING_STATUS" />
          </Checkbox>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="standard"
          size="m"
          onClick={close}
          testId={`${mode}-element-status-cancel`}
        >
          <FormattedMessage id="global.cancel" />
        </Button>
        <Button
          size="m"
          variant="primary"
          disabled={isSubmitDisabled()}
          onClick={submit}
          testId={`${mode}-element-status-submit`}
          style={{ margin: '0 0 0 1rem' }}
        >
          <FormattedMessage id={MESSAGES[mode].button} />
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ElementStatusFormModal;
